import { CosmosDb } from 'lib/cosmos/cosmosDb.js';
import { DietDb } from 'lib/cosmos/diet/dietDb.js';
import { FoodDb } from 'lib/cosmos/food/foodDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { pluralHelper } from 'lib/helpers/plural.helper.class.js';
import { map, of } from 'rxjs';
import { DietFormComponent } from 'src/app/modules/diets/diet-form.component.js';
import { ListDietsComponent } from 'src/app/modules/diets/list-diets.component.js';
import { ViewOrEditDietComponent } from 'src/app/modules/diets/view-or-edit-diet.component.js';
import { CoreAppComponentDependencies } from '../../core/core-app-dependencies.js';
import { foodHelper } from '../../modules/foods/food.helper.js';
import {
	ICoreRoutes,
	IPageMetadata,
	ISharedData,
	ITypePageCard,
	ITypeParentRoute,
	ITypeRoute,
	ITypeRouteAction,
	RouteInitializer
} from '../../pages/type-routes/type-route.models.js';

export interface IViewOrEditDietPlanRouteData {
	diet: DietDb.IDietItem;
	isDietDesigner: boolean;
	clientData:
		| undefined
		| {
				client: UserDb.IUserItem;
				dietContainer: UserDb.IUserDietContainer;
				dietItem: UserDb.IUserDietItem;
		  };
}

export interface IDietRoutes extends ICoreRoutes {
	diets: ITypeRoute<
		IViewOrEditDietPlanRouteData,
		{
			custom: ITypeRouteAction<undefined>;
			new: ITypeRouteAction<undefined>;
		}
	>;
	diet: ITypeRoute<
		IViewOrEditDietPlanRouteData,
		{
			view: ITypeRouteAction<IViewOrEditDietPlanRouteData>;
			edit: ITypeRouteAction<IViewOrEditDietPlanRouteData>;
			design: ITypeRouteAction<IViewOrEditDietPlanRouteData>;
		}
	>;
}

export const dietRoutes: RouteInitializer<IDietRoutes> = {
	diets: {
		mainPath: 'trainer',
		uniqueId: 'diets',
		parentRoutes: (context) => [],
		loadSharedData: (context) => {
			return of(undefined);
		},
		menuItems: (context) => [],
		children: {
			custom: {
				uniqueId: `customDiets`,
				component: ListDietsComponent,
				getMetadata: (data) => {
					const metadata: IPageMetadata = {
						title: `Diets`
					};
					return metadata;
				}
			},
			new: {
				parentRoutes: (context) => {
					return [
						{
							routerLink: context.dependencies.navigateService
								.actionRoute((routes) => routes.dietRoutes.diets.children.custom)
								.getUrl(),
							title: `Diets`
						}
					];
				},
				uniqueId: `newDiet`,
				component: DietFormComponent,
				getMetadata: (data) => {
					const metadata: IPageMetadata = {
						title: `New`
					};
					return metadata;
				}
			}
		}
	},
	diet: {
		uniqueId: 'diet',
		mainPath: 'trainer',
		parentRoutes: (context) => {
			const itemRoutes: ITypeParentRoute[] = [];

			return [
				{
					routerLink: context.dependencies.navigateService
						.actionRoute((routes) => routes.dietRoutes.diets.children.custom)
						.getUrl(),
					title: `Diet Templates`
				},
				...itemRoutes
			];
		},
		loadSharedData: (context) => {
			return context.dependencies.httpService
				.get<DietDb.IDietItem>((builder) =>
					builder.withCustomApiRoute('diet', 'getPlan').withId(context.routeData.primaryId).build()
				)
				.pipe(
					map((response) => {
						const shared: ISharedData<IViewOrEditDietPlanRouteData> = {
							data: {
								diet: response.data,
								isDietDesigner: false,
								clientData: undefined
							},
							linkedItems: response.linkedItems
						};

						return shared;
					})
				);
		},
		menuItems: (context) => [
			{
				routerLink: context.dependencies.navigateService
					.actionRoute((routes) => routes.dietRoutes.diet.children.design, context.routeData.primaryId)
					.getUrl(),
				title: `Design`,
				iconType: 'design'
			},
			{
				routerLink: context.dependencies.navigateService
					.actionRoute((routes) => routes.dietRoutes.diet.children.view, context.routeData.primaryId)
					.getUrl(),
				title: `Preview`,
				iconType: 'preview'
			}
			// {
			// 	routerLink: context.dependencies.navigateService
			// 		.actionRoute((routes) => routes.dietRoutes.diet.children.edit, context.routeData.primaryId)
			// 		.getUrl(),
			// 	title: `Notes`,
			// 	iconType: 'text'
			// }
		],
		children: {
			edit: {
				uniqueId: `editDiet`,
				component: DietFormComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: `Edit`,
						card: getCard(
							context.dependencies,
							context.sharedData?.data?.diet,
							context.sharedData?.linkedItems ?? []
						)
					};

					return metadata;
				}
			},
			view: {
				uniqueId: `viewDiet`,
				component: ViewOrEditDietComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: `Preview`,
						card: getCard(
							context.dependencies,
							context.sharedData?.data?.diet,
							context.sharedData?.linkedItems ?? []
						)
					};

					return metadata;
				},
				processSharedData: (context) => {
					context.sharedData.data.isDietDesigner = false;
					return context.sharedData;
				}
			},
			design: {
				uniqueId: `designDiet`,
				component: ViewOrEditDietComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: `Design`,
						card: getCard(
							context.dependencies,
							context.sharedData?.data?.diet,
							context.sharedData?.linkedItems ?? []
						)
					};

					return metadata;
				},
				processSharedData: (context) => {
					context.sharedData.data.isDietDesigner = true;
					return context.sharedData;
				}
			}
		}
	}
};

function getCard(
	dependencies: CoreAppComponentDependencies,
	diet: DietDb.IDietItem | undefined,
	linkedItems: CosmosDb.ICosmosItem[]
): ITypePageCard | undefined {
	if (!diet) {
		return undefined;
	}

	return {
		title: dependencies.coreAppService.getTitleCardConfig<IViewOrEditDietPlanRouteData>({
			updateSharedData: (title, sharedData) => {
				if (sharedData.data.diet) {
					sharedData.data.diet.system.name = title;
					sharedData.data.diet.system.updated = dependencies.timeService.nowCosmosDb();
				}
				return sharedData;
			},
			item: diet
		}),
		avatar: {
			avatarName: diet.system.name,
			avatar: diet.avatar?.[0]?.image,
			upload: dependencies.coreAppService.getAvatarCardUploadConfig<
				DietDb.IDietItem,
				IViewOrEditDietPlanRouteData
			>({
				type: 'diet',
				existingAvatar: diet?.avatar?.[0],
				patchModel: (file) => {
					return {
						id: diet.id,
						avatar: file ? [file] : undefined
					};
				},
				updateSharedData: (file, sharedData) => {
					if (sharedData.data.diet) {
						sharedData.data.diet.avatar = file ? [file] : [];
						sharedData.data.diet.system.updated = dependencies.timeService.nowCosmosDb();
					}

					return sharedData;
				}
			})
		},
		nutrition: foodHelper.calculateFoodNutrientsForDiet(diet, linkedItems as FoodDb.IFoodItem[]),
		tags: [
			{
				icon: 'food',
				text: pluralHelper.getPluralText({
					count: diet.foodContainers.length,
					m0: `0 meals`,
					m1: `1 meal`,
					m5: `${diet.foodContainers.length} meals`
				})
			}
		],
		updated: diet.system.updated,
		layoutStyle: 'default',
		buttons: [
			{
				title: 'More',
				nestedButtons: [
					{
						title: 'Download PDF',
						click: () => {
							dependencies.dialogService.openPdfDialog({
								generatePdf: () =>
									dependencies.httpService.generateDietPdf({
										diet: diet,
										userId: undefined,
										containerItemId: undefined
									})
							});
						}
					},
					{
						title: 'Delete',
						confirm: {
							confirmMode: 'delete',
							text: `Are you sure to delete '${diet.system.name}'?`
						},
						clickAsync: () => {
							return dependencies.httpService.deleteSingle('diet', diet.id).pipe(
								map((response) => {
									dependencies.notificationService.notifyDeleted('Diet');

									dependencies.navigateService
										.actionRoute((appRoutes) => appRoutes.dietRoutes.diets.children.custom)
										.navigate();
								})
							);
						}
					}
				]
			}
		]
	};
}
